import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { formatFileSize, formatUploader, formatDateShort } from '../../../core/components/ui/utils/formatters';

/**
 * File Card Component (Mobile View)
 *
 * Displays a file or folder in a card layout optimized for mobile devices.
 * Shows all information in a vertical stacked format.
 * Uses shared formatters from core/components/ui/utils.
 *
 * @param {Object} props
 * @param {Object} props.file - File or folder object
 * @param {Function} props.onDownload - Download handler (files only)
 * @param {Function} props.onDelete - Delete handler
 * @param {Function} props.onRename - Rename handler (folders only)
 * @param {Function} props.onMove - Move handler (files only)
 * @param {boolean} props.isDownloading - Whether this file is currently downloading
 *
 * @example
 * <FileCard
 *   file={file}
 *   onDownload={() => handleDownload(file)}
 *   onDelete={() => handleDelete(file)}
 *   onRename={() => handleRename(file)}
 *   onMove={() => handleMove(file)}
 *   isDownloading={downloadingFile === file.id}
 * />
 */
export function FileCard({ file, onDownload, onDelete, onRename, onMove, isDownloading }) {
  const { t } = useTranslation();
  const isFolder = file.type === 'folder';
  const name = isFolder ? file.name : file.original_filename;

  return (
    <div className="border border-border-default rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
      {/* Name with icon */}
      <div className="mb-3 flex items-center gap-2">
        {isFolder && (
          <Icon icon="mdi:folder" className="w-5 h-5 text-brand flex-shrink-0" />
        )}
        <h3 className="text-base font-semibold text-default break-words line-clamp-2">
          {name}
        </h3>
      </div>

      {/* Details */}
      <div className="space-y-2 mb-4">
        {!isFolder && (
          <>
            {/* Type */}
            <div className="flex items-start">
              <span className="text-xs font-medium text-muted uppercase w-20 flex-shrink-0">
                {t('file.type', 'Type')}:
              </span>
              <span className="text-sm text-muted break-words flex-1">
                {file.content_type}
              </span>
            </div>

            {/* Size */}
            <div className="flex items-start">
              <span className="text-xs font-medium text-muted uppercase w-20 flex-shrink-0">
                {t('file.size', 'Size')}:
              </span>
              <span className="text-sm text-muted">
                {formatFileSize(file.file_size || 0)}
              </span>
            </div>
          </>
        )}

        {isFolder && (
          /* File count */
          <div className="flex items-start">
            <span className="text-xs font-medium text-muted uppercase w-20 flex-shrink-0">
              {t('file.fileCount', 'Archivos')}:
            </span>
            <span className="text-sm text-muted">
              {file.file_count || 0}
            </span>
          </div>
        )}

        {/* Creator/Uploader */}
        <div className="flex items-start">
          <span className="text-xs font-medium text-muted uppercase w-20 flex-shrink-0">
            {t('file.uploader', 'By')}:
          </span>
          <span className="text-sm text-muted">
            {formatUploader(isFolder ? file.created_by : file.uploaded_by, t)}
          </span>
        </div>

        {/* Date */}
        <div className="flex items-start">
          <span className="text-xs font-medium text-muted uppercase w-20 flex-shrink-0">
            {t('file.uploadTime', 'Date')}:
          </span>
          <span className="text-sm text-muted">
            {formatDateShort(file.time_updated || file.time_created)}
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2 pt-3 border-t border-border-subtle">
        {isFolder ? (
          <>
            {/* Rename Button */}
            <button
              type="button"
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-border-default text-brand hover:bg-brand/5 focus:outline-none focus:bg-brand/5 cursor-pointer"
              onClick={onRename}
              aria-label={t('files.explorer.rename', 'Renombrar')}
            >
              <Icon icon="mdi:pencil" className="size-4" />
              <span>{t('files.explorer.rename', 'Renombrar')}</span>
            </button>

            {/* Delete Button */}
            <button
              type="button"
              className="inline-flex items-center justify-center size-10 text-sm font-medium rounded-lg border border-border-default text-error hover:bg-error/5 focus:outline-none focus:bg-error/5 cursor-pointer"
              onClick={onDelete}
              aria-label={t('files.explorer.delete', 'Eliminar')}
            >
              <Icon icon="mdi:delete" className="size-4" />
            </button>
          </>
        ) : (
          <>
            {/* Download Button */}
            <button
              type="button"
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-border-default text-brand hover:bg-brand/5 disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:bg-brand/5 cursor-pointer"
              onClick={onDownload}
              disabled={isDownloading}
              aria-label={t('files.explorer.download', 'Descargar')}
            >
              <Icon icon="mdi:download" className="size-4" />
              <span>{t('files.explorer.download', 'Descargar')}</span>
            </button>

            {/* Move Button */}
            <button
              type="button"
              className="inline-flex items-center justify-center size-10 text-sm font-medium rounded-lg border border-border-default text-default hover:bg-brand/5 focus:outline-none focus:bg-brand/5 cursor-pointer"
              onClick={onMove}
              aria-label={t('files.explorer.moveToFolder', 'Mover a carpeta')}
            >
              <Icon icon="mdi:folder-move" className="size-4" />
            </button>

            {/* Delete Button */}
            <button
              type="button"
              className="inline-flex items-center justify-center size-10 text-sm font-medium rounded-lg border border-border-default text-error hover:bg-error/5 focus:outline-none focus:bg-error/5 cursor-pointer"
              onClick={onDelete}
              aria-label={t('files.explorer.delete', 'Eliminar')}
            >
              <Icon icon="mdi:delete" className="size-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

FileCard.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['file', 'folder']),
    original_filename: PropTypes.string,
    name: PropTypes.string,
    content_type: PropTypes.string,
    file_size: PropTypes.number,
    file_count: PropTypes.number,
    time_created: PropTypes.string.isRequired,
    time_updated: PropTypes.string,
    uploaded_by: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        username: PropTypes.string,
      }),
    ]),
    created_by: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        username: PropTypes.string,
      }),
    ]),
  }).isRequired,
  onDownload: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  onRename: PropTypes.func,
  onMove: PropTypes.func,
  isDownloading: PropTypes.bool,
};
